import React, { useState } from 'react';
import { Box, ListSubheader, Menu, MenuItem, Typography } from '@mui/material';
import DosenTooltip from '../../../../../components/ui/DosenTooltip';
import { lessonCapability, type LessonTagChoice, type LessonTags, type TaggedLesson } from '../lessonTags';

const lineSx = {
  fontSize: 11,
  fontFamily: 'Inter',
  color: 'var(--Text-text-tertiary, #868686)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
} as const;

const menuHeaderSx = {
  bgcolor: '#1A1A1A',
  fontSize: 11,
  fontFamily: 'Inter',
  fontWeight: 600,
  lineHeight: '28px',
  color: 'var(--Text-text-tertiary, #868686)'
};

/**
 * Under a lesson on the Structure step (the build flow): the capability it
 * builds, or the learning outcomes it serves, and Graded Roleplay where the
 * capability's Roleplay is. A lesson added by hand in a program built on
 * capabilities asks what it is for; until it is told, the backend makes it
 * serve the outcomes.
 */
const LessonTagLine: React.FC<{
  lesson: TaggedLesson;
  roleplay: boolean;
  tags: LessonTags;
  /** Can be told what it is for (the admin holds the edit lock). */
  editable: boolean;
  onTag: (choice: LessonTagChoice) => void;
}> = ({ lesson, roleplay, tags, editable, onTag }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const capability = lessonCapability(lesson, tags);
  const outcomes = capability ? [] : lesson.outcomes ?? [];
  const choose = (choice: LessonTagChoice) => {
    setAnchor(null);
    onTag(choice);
  };

  let body: React.ReactNode = null;
  if (capability) {
    body = <Typography sx={lineSx}>Builds {capability.name}</Typography>;
  } else if (outcomes.length > 0) {
    body = (
      <DosenTooltip title={outcomes.join('; ')} placement="bottom" enterDelay={400}>
        <Typography sx={{ ...lineSx, cursor: 'default' }}>Serves {outcomes.join('; ')}</Typography>
      </DosenTooltip>
    );
  } else if (tags.capabilities.length > 0 && editable) {
    body = (
      <Box
        component="button"
        type="button"
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          // Not a click on the lesson itself.
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        sx={{
          ...lineSx,
          p: 0,
          border: 'none',
          bgcolor: 'transparent',
          color: 'var(--Stroke-highlight, #F79420)',
          cursor: 'pointer',
          '&:hover': { textDecoration: 'underline' }
        }}
      >
        Choose what this Lesson builds
      </Box>
    );
  } else if (tags.outcomes.length > 0) {
    // Untagged, it is saved serving every one of them.
    body = <Typography sx={lineSx}>Serves your goals</Typography>;
  }
  if (!body && !roleplay) return null;

  return (
    <Box
      data-testid="lesson-tag"
      data-capability-id={capability?.id ?? ''}
      sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, mt: '2px' }}
    >
      <Box sx={{ minWidth: 0, display: 'flex' }}>{body}</Box>
      {roleplay && (
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            px: '5px',
            borderRadius: '4px',
            border: '1px solid rgba(247, 148, 32, 0.45)',
            bgcolor: 'rgba(247, 148, 32, 0.1)',
            color: 'var(--Stroke-highlight, #F79420)',
            fontSize: 10,
            fontWeight: 600,
            fontFamily: 'Inter',
            lineHeight: '16px',
            whiteSpace: 'nowrap'
          }}
        >
          Scored Roleplay
        </Box>
      )}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{ sx: { background: '#1A1A1A', border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)' } }}
      >
        <ListSubheader sx={menuHeaderSx}>Builds a capability</ListSubheader>
        {tags.capabilities.map((c) => (
          <MenuItem
            key={c.id}
            onClick={() => choose({ capabilityId: c.id })}
            sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}
          >
            {c.name}
          </MenuItem>
        ))}
        {tags.outcomes.length > 0 && <ListSubheader sx={menuHeaderSx}>Serves a goal</ListSubheader>}
        {tags.outcomes.map((o) => (
          <MenuItem
            key={o}
            onClick={() => choose({ outcome: o })}
            sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}
          >
            {o}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default LessonTagLine;
